import React, { useState, useRef, useEffect } from "react";
import { collection, onSnapshot, query, where, getDocs, doc, updateDoc, addDoc, serverTimestamp } from "firebase/firestore";
import { db, auth } from "./firebase";
import { usePostsByIds, getPostClosure, formatPostDate } from "./useEligibleOjtPosts";
import { useDepartmentsPrograms } from "./departmentsPrograms";
import blackUserIcon from "../icons/blackuser.png";
import blackViewIcon from "../icons/blackview.png"; // default/fallback — the themed icon itself comes in via the viewIcon prop
import { color, font, type, space, radius, shadow, ease } from "./theme";
import { standingOf, STANDING_TONE } from "./accountStanding";

// ── Design tokens, aliased for this screen ────────────────────────────────────
// Same aliases as CoordinatorFindCompanyScreen so the two screens stay in sync.
const ink        = color.ink;
const inkBody    = color.inkBody;
const inkMuted   = color.inkMuted;
const inkFaint   = color.inkFaint;
const surface    = color.wine600;      // rows, cards
const page       = color.wine900;      // page background
const line       = color.wine700;      // hairlines & borders
// stronger border for buttons & pills (the plain `line` is too faint on them)
const lineStrong = "#7A7A7A";
const lineSoft   = color.wine800;
const panel      = color.blush100;     // dark header bar
const panelDeep  = color.blush50;
const onPanel    = color.onWine;
const onPanelDim = color.onWineMuted;

// ── Batch ────────────────────────────────────────────────────────────────────
// Same field and rules as the Student Accounts screen (CoordinatorStudents-
// AcccountScreen.jsx): students/{uid}.batch is the ACADEMIC YEAR ("2026-2027"),
// which is not the same as yearSection ("4-A"). Older records have no batch, so
// they group under "No batch set" rather than disappearing.
const BATCH_NONE     = "__none__";
const batchKeyOf     = (s) => { const b = String(s?.batch || "").trim(); return b || BATCH_NONE; };
const batchLabelOf   = (key) => (key === BATCH_NONE ? "No batch set" : `Batch ${key}`);
const batchStartYear = (key) => Number(String(key).split("-")[0]) || 0;
const sortBatchKeys  = (keys) => [...keys].sort((a, b) => {
  if (a === BATCH_NONE) return 1;
  if (b === BATCH_NONE) return -1;
  return batchStartYear(b) - batchStartYear(a);
});

// Colors for the small status pill shown on each student row / placement modal
const STATUS_COLORS = {
  "Accepted":    { bg: color.success, color: color.white },
  "Declined":    { bg: color.danger,  color: color.white },
  "Pending":     { bg: color.wine400, color: ink },
  "In Review":   { bg: color.warning, color: color.white },
  "To Interview":{ bg: color.info,    color: color.white },
  "Withdrawn":   { bg: "#6B6B6B",     color: color.white },   // cancelled by the student; record kept
};

// ── Follow-up rules (OJTern affiliation workflow) ────────────────────────────
// An application needs the coordinator's attention when:
//   • the company withdrew the student's program while the application was
//     still open or already accepted (status is NOT changed automatically), or
//   • the student withdrew from a placement that was already ACCEPTED.
const CLOSED_STATUSES = ["Declined", "Withdrawn"];
// The coordinator reviews a placement once the STUDENT has confirmed it (a
// student may be accepted by several companies and picks one).
const needsPlacementReview = (app) =>
  !!app && app.status === "Accepted" && app.placementConfirmed === true && !app.coordinatorReviewedAt;
// Only while the student has NOT confirmed any placement yet. An acceptance
// that arrives after the student already confirmed elsewhere is just noted.
const confirmedOf = (apps) => (apps || []).find(a => a.status === "Accepted" && a.placementConfirmed === true) || null;
const awaitingConfirmation = (app, apps = []) =>
  !!app && app.status === "Accepted" && app.placementConfirmed !== true && !confirmedOf(apps);
const followUpReason = (app, apps = [], standings = {}) => {
  if (!app) return null;
  // A placement whose company has been suspended or blocked is at risk, so it
  // belongs in Needs Follow-up — the student can't resolve that themselves.
  const standing = standings[app.companyId];
  if (standing && !standing.isActive && !CLOSED_STATUSES.includes(app.status)) {
    return standing.state === "suspended"
      ? `Company suspended${standing.endDate ? ` until ${standing.endDate}` : ""} — application on hold`
      : "Company blocked — this application can no longer proceed";
  }
  if (needsPlacementReview(app)) return "Confirmed placement — review the company and post";
  if (app.status === "Withdrawn" && app.statusBeforeWithdrawal === "Accepted") {
    return "Student withdrew from an accepted placement";
  }
  if (app.affiliationWithdrawn && !CLOSED_STATUSES.includes(app.status)) {
    return app.status === "Accepted"
      ? "Accepted, but the company withdrew this program"
      : "Company withdrew this program — application still open";
  }
  if (awaitingConfirmation(app, apps)) return "Accepted — waiting for the student to confirm their placement";
  return null;
};
const needsFollowUp = (apps, standings = {}) => (apps || []).some(a => !!followUpReason(a, apps, standings));

// When a student has more than one application, this decides which one
// "represents" them at a glance (list row badge) — most-advanced/most-
// relevant status wins, rather than whichever doc Firestore happened to
// return first. Declined only wins if every single application was
// declined (see matchesStatusFilter below).
const STATUS_PRIORITY = ["Accepted", "To Interview", "In Review", "Pending", "Declined", "Withdrawn"];

// Full name, assembled the same way everywhere it appears — the list row, the
// placement modal, and the PDF export. Kept in one place so a name can never
// read differently depending on where a coordinator happens to be looking.
const getFullName = (s) =>
  `${s.firstName} ${s.middleInitial ? s.middleInitial + " " : ""}${s.lastName}` +
  `${s.suffix && s.suffix !== "None" && s.suffix !== "N/A" ? " " + s.suffix : ""}`;

// Alphabetical by last name, then first name, then middle initial — the usual
// class-list order. localeCompare with sensitivity "base" ignores case and
// accents ("de la Cruz" / "Dela Cruz", "Ñ"), and students missing a name sort last
// within their group rather than throwing.
const compareStudentNames = (a, b) => {
  const cmp = (x, y) => {
    const X = String(x || "").trim(), Y = String(y || "").trim();
    if (!X || !Y) return X === Y ? 0 : X ? -1 : 1; // blank names sort last
    return X.localeCompare(Y, undefined, { sensitivity: "base", numeric: true });
  };
  return cmp(a.lastName, b.lastName) || cmp(a.firstName, b.firstName) || cmp(a.middleInitial, b.middleInitial);
};

const getBestApplication = (apps) => {
  if (!apps || apps.length === 0) return null;
  for (const status of STATUS_PRIORITY) {
    const found = apps.find(a => a.status === status);
    if (found) return found;
  }
  return apps[0];
};

// Non-exclusive filter check — a student can match more than one filter
// at once (e.g. Accepted somewhere AND still has a Pending application
// elsewhere shows up under both "Accepted" and "In Progress").
const matchesStatusFilter = (apps, filterValue, standings = {}) => {
  if (!filterValue) return true; // "All"
  if (filterValue === "No Applications yet") return !apps || apps.length === 0;
  if (!apps || apps.length === 0) return false;
  if (filterValue === "Accepted") return apps.some(a => a.status === "Accepted");
  if (filterValue === "In Progress") return apps.some(a => ["Pending", "In Review", "To Interview"].includes(a.status));
  if (filterValue === "All Declined") return apps.every(a => a.status === "Declined");
  if (filterValue === "Needs Follow-up") return needsFollowUp(apps, standings);
  return true;
};

// ── Export ────────────────────────────────────────────────────────────────────

// Student ID is the FIRST column of every PDF, then the name. Keep studentBase() and buildExportRows() in step with these headers.
const EXPORT_HEADER = ["Student ID", "Student Name", "College", "Program", "Year & Section", "Placement", "Status"];

// Three export modes (chosen from the Export menu):
//   "final"   → Final placements: only students with an Accepted application,
//               one row each — the company they'll be deployed to once they
//               confirmed it, or every accepted company marked "not yet
//               confirmed by student".
//   "status"  → Placement status: every student, one row — the placement, or
//               "No placement yet" with their current progress.
//   "history" → Full application history: one row per application.
const EXPORT_MODES = {
  final:   { title: "Final OJT Placements",    unit: "placement",          header: ["Student ID", "Student Name", "College", "Program", "Year & Section", "Company (Deployment)", "Accepted / Confirmed", "Reviewed by Coordinator"] },
  status:  { title: "Student Placement Status", unit: "student",           header: ["Student ID", "Student Name", "College", "Program", "Year & Section", "Placement", "Status"] },
  history: { title: "Student Application History", unit: "application record", header: EXPORT_HEADER },
};

const fmtTs = (ts) => (ts?.seconds ? new Date(ts.seconds * 1000).toLocaleDateString("en-US", { month: "short", day: "2-digit", year: "numeric" }) : "");
const companyNameOf = (app, companies) => companies.find(c => c.id === app.companyId)?.name || app.companyName || "Unknown company";

// companyId -> standing, built from the companies already loaded on this
// screen, so no extra Firestore listeners are needed.
const standingsByCompany = (companies = []) => {
  const map = {};
  companies.forEach(c => { map[c.id] = standingOf(c, "company"); });
  return map;
};
const placementNotes = (app, postsById) => {
  const notes = [];
  const closure = getPostClosure(app.postId, postsById);
  if (closure) notes.push(closure.label.toLowerCase());
  if (app.affiliationWithdrawn) notes.push("company is no longer in the program");
  return notes;
};
const studentBase = (student) => [
  student.studentId || "", getFullName(student), student.college || "", student.program || "", student.yearSection || "",
];

const buildFinalPlacementRows = (students, applicationsByStudent, companies, postsById = {}) =>
  students.flatMap(student => {
    const accepted = (applicationsByStudent[student.id] || []).filter(a => a.status === "Accepted");
    if (accepted.length === 0) return [];
    const confirmed = accepted.find(a => a.placementConfirmed === true);
    if (confirmed) {
      const notes = placementNotes(confirmed, postsById);
      return [[
        ...studentBase(student),
        `${companyNameOf(confirmed, companies)}${notes.length ? ` (${notes.join("; ")})` : ""}`,
        `Accepted ${fmtTs(confirmed.acceptedAt) || ""} · Confirmed ${fmtTs(confirmed.placementConfirmedAt) || ""}`.replace(/\s+·/g, " ·").trim(),
        confirmed.coordinatorReviewedAt ? `Reviewed ${fmtTs(confirmed.coordinatorReviewedAt)}` : "Not yet reviewed",
      ]];
    }
    return [[
      ...studentBase(student),
      accepted.map(a => companyNameOf(a, companies)).join(", "),
      accepted.length > 1 ? `Multiple acceptances — not yet confirmed by student` : "Accepted — not yet confirmed by student",
      "—",
    ]];
  });

const STATUS_RANK = { "To Interview": 3, "In Review": 2, "Pending": 1 };
const buildPlacementStatusRows = (students, applicationsByStudent, companies, postsById = {}) =>
  students.map(student => {
    const apps = applicationsByStudent[student.id] || [];
    const base = studentBase(student);
    if (apps.length === 0) return [...base, "No placement yet", "No applications yet"];
    const accepted = apps.filter(a => a.status === "Accepted");
    const confirmed = accepted.find(a => a.placementConfirmed === true);
    if (confirmed) {
      const notes = placementNotes(confirmed, postsById);
      return [...base, companyNameOf(confirmed, companies), `Confirmed placement${confirmed.coordinatorReviewedAt ? ", reviewed" : ", not yet reviewed"}${notes.length ? ` (${notes.join("; ")})` : ""}`];
    }
    if (accepted.length) {
      return [...base, accepted.map(a => companyNameOf(a, companies)).join(", "), accepted.length > 1 ? "Multiple acceptances — not yet confirmed" : "Accepted — not yet confirmed"];
    }
    const active = apps.filter(a => STATUS_RANK[a.status]).sort((a, b) => STATUS_RANK[b.status] - STATUS_RANK[a.status]);
    if (active.length) {
      const top = active[0];
      const more = active.length - 1;
      return [...base, "No placement yet", `${top.status} at ${companyNameOf(top, companies)}${more ? ` (+${more} other application${more > 1 ? "s" : ""})` : ""}`];
    }
    return [...base, "No placement yet", "All applications declined/withdrawn"];
  });

// One row per APPLICATION, not per student: a student who applied to three
// companies is genuinely three placement records, and collapsing them to the
// single "best" one (as the list row badge does) would silently drop the rest
// from the export. Students with no applications still get one row, so the
// export and the on-screen count always agree.
//
const buildExportRows = (students, applicationsByStudent, companies, postsById = {}) =>
  students.flatMap(student => {
    const base = [
      student.studentId || "",
      getFullName(student),
      student.college || "",
      student.program || "",
      student.yearSection || "",
    ];

    const apps = applicationsByStudent[student.id] || [];
    if (apps.length === 0) return [[...base, "—", "No applications yet"]];

    return apps.map(app => {
      const company = companies.find(c => c.id === app.companyId);
      const notes = [];
      if (app.status === "Withdrawn" && app.statusBeforeWithdrawal) notes.push(`was ${app.statusBeforeWithdrawal}`);
      if (app.status === "Withdrawn" && app.withdrawalReason) notes.push(`reason: ${app.withdrawalReason}`);
      const closure = getPostClosure(app.postId, postsById);
      if (closure && app.status !== "Withdrawn") notes.push(closure.label.toLowerCase());
      const exportStanding = company ? standingOf(company, "company") : null;
      if (exportStanding && !exportStanding.isActive) {
        notes.push(exportStanding.state === "suspended"
          ? `company suspended${exportStanding.endDate ? ` until ${exportStanding.endDate}` : ""}`
          : "company blocked");
      }
      if (app.affiliationWithdrawn) notes.push("company is no longer in the program");
      else if (app.affiliationRestoredAt) notes.push("company is no longer in the program, then restored");
      return [
        ...base,
        company?.name || app.companyName || "Unknown company",
        `${app.status || ""}${notes.length ? ` (${notes.join("; ")})` : ""}`,
      ];
    });
  });

// Human-readable summary of what's currently narrowing the list, printed under
// the PDF title. Without it a printed export is unfalsifiable — a coordinator
// holding a 12-row page has no way to tell whether that's the whole cohort or
// the leftovers of four active filters.
const describeExportScope = (filters, search) => {
  const parts = [];
  if (search.trim())          parts.push(`Search: "${search.trim()}"`);
  if (filters.college)        parts.push(filters.college);
  if (filters.program)        parts.push(filters.program);
  if (filters.specialization) parts.push(filters.specialization);
  if (filters.sex)            parts.push(filters.sex);
  if (filters.section)        parts.push(`Section ${filters.section}`);
  if (filters.status)         parts.push(filters.status);
  if (filters.batch)          parts.push(batchLabelOf(filters.batch));
  return parts.length ? parts.join(" · ") : "No filters applied";
};

const downloadBlob = (filename, blob) => {
  const url  = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

// jsPDF and its autoTable plugin are ~400KB together, and exporting is a rare,
// deliberate action — loading them eagerly would make every coordinator pay
// that cost on first paint just so the button exists. Imported on click
// instead, so the weight lands only on whoever actually exports.
// `groups` is [{ label, studentCount, rows }]. With more than one group (the
// "All batches" export) each batch gets its own heading and table, so the
// printed list reads batch by batch instead of one long mixed table.
const buildStudentPdf = async ({ groups, scope, total, mode = "history" }) => {
  const cfg = EXPORT_MODES[mode] || EXPORT_MODES.history;
  const { jsPDF }     = await import("jspdf");
  const { default: autoTable } = await import("jspdf-autotable");

  // Landscape: seven columns, and program names like "BS Information
  // Technology" wrap into unreadable slivers at portrait width.
  const doc    = new jsPDF({ orientation: "landscape", unit: "pt", format: "a4" });
  const width  = doc.internal.pageSize.getWidth();
  const margin = 40;

  doc.setFont("helvetica", "bold");
  doc.setFontSize(16);
  doc.text(cfg.title, margin, 46);

  doc.setFont("helvetica", "normal");
  doc.setFontSize(9);
  doc.setTextColor(110);
  doc.text(scope, margin, 62);
  const totalRows = groups.reduce((n, g) => n + g.rows.length, 0);
  doc.text(
    `${total} student${total === 1 ? "" : "s"} in this list · ${totalRows} ${cfg.unit}${totalRows === 1 ? "" : "s"} · Generated ${new Date().toLocaleDateString("en-US", { month: "short", day: "2-digit", year: "numeric" })}`,
    margin, 75
  );
  doc.setTextColor(0);

  const pageHeight = doc.internal.pageSize.getHeight();
  let cursorY = 92;

  groups.forEach((group, gi) => {
  if (groups.length > 1) {
    if (gi > 0) cursorY += 22;
    // Don't strand a batch heading at the very bottom of a page.
    if (cursorY > pageHeight - 110) { doc.addPage(); cursorY = 46; }
    doc.setFont("helvetica", "bold");
    doc.setFontSize(11);
    doc.setTextColor(0);
    doc.text(
      `${group.label} · ${group.studentCount} student${group.studentCount === 1 ? "" : "s"} · ${group.rows.length} ${cfg.unit}${group.rows.length === 1 ? "" : "s"}`,
      margin, cursorY
    );
    cursorY += 8;
  }

  autoTable(doc, {
    head: [cfg.header],
    body: group.rows,
    startY: cursorY,
    margin: { left: margin, right: margin, bottom: 46 },
    styles:     { font: "helvetica", fontSize: 8.5, cellPadding: 5, overflow: "linebreak" },
    headStyles: { fillColor: [139, 0, 0], textColor: 255, fontStyle: "bold" },
    alternateRowStyles: { fillColor: [248, 246, 246] },
    // Column widths are left to autoTable. Pinning them by hand looks tidier
    // in source but fights the layout engine: fixed widths are treated as
    // minimums, so long values like "BS Business Administration major in
    // Marketing Management" push the table past the page edge and autoTable
    // drops the overflow. Auto-sizing distributes by real content and fits.
    columnStyles: { 0: { minCellWidth: 62 }, 1: { minCellWidth: 110 }, 3: { minCellWidth: 120 }, 5: { minCellWidth: 110 } },
    // A multi-page placement list is easy to shuffle out of order once it is
    // printed, so every page carries its own number.
    didDrawPage: () => {
      const page   = doc.internal.getNumberOfPages();
      const height = doc.internal.pageSize.getHeight();
      doc.setFont("helvetica", "normal");
      doc.setFontSize(8);
      doc.setTextColor(130);
      doc.text(`OJTern · Page ${page}`, width - margin, height - 24, { align: "right" });
      doc.setTextColor(0);
    },
  });
  cursorY = doc.lastAutoTable?.finalY ?? cursorY;
  });

  return doc.output("blob");
};

// ── Responsive styles ─────────────────────────────────────────────────────────
// Page shape mirrors Find Company (padded scroll area → floating dark bar →
// chips), but the students themselves stay a full-width horizontal list.
const ResponsiveStyles = () => (
  <style>{`
    @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600&display=swap');
    * { box-sizing: border-box; margin: 0; padding: 0; }
    ::-webkit-scrollbar { width: 6px; }
    ::-webkit-scrollbar-thumb { background: ${color.wine400}; border-radius: 999px; }
    ::-webkit-scrollbar-track { background: transparent; }

    /* List wrapper: vertical scroll only */
    .sp-list-wrapper {
      overflow: hidden;
      display: flex;
      flex-direction: column;
      min-height: 0;
      width: 100%;
      flex: 1;
      background: ${page};
      padding: clamp(16px, 4vw, 28px) clamp(16px, 4vw, 32px);
    }

    /* Floating dark header bar */
    .sp-search-bar {
      background: ${panel};
      border-radius: ${radius.panel};
      padding: 18px 22px;
      margin-bottom: ${space.md};
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: ${space.md};
      flex-wrap: nowrap;
    }
    @media (max-width: 480px) {
      .sp-search-bar { padding: 14px; gap: 10px; }
    }

    .sp-search-input { width: 170px; }
    .sp-search-input::placeholder { color: ${inkFaint}; }
    .sp-search-input:focus,
    .sp-search-input:focus-visible {
      outline: none;
      box-shadow: none;
      -webkit-box-shadow: none;
    }
    @media (max-width: 480px) {
      .sp-search-input { width: 90px; }
    }
    @media (max-width: 380px) {
      .sp-search-input { width: 62px; }
    }

    /* Full-width student rows */
    /* Same card rows as Students Account (.sa-row there): rounded card,
       roomier padding, bold name over a single "·"-joined meta line. */
    .sp-rows {
      display: flex;
      flex-direction: column;
      gap: 10px;
    }
    .sp-row {
      display: flex;
      align-items: center;
      gap: 14px;
      background: ${surface};
      border: 1px solid ${line};
      border-radius: ${radius.card};
      padding: 14px 20px;
      cursor: pointer;
      min-width: 0;
      box-shadow: ${shadow.input};
      transition: border-color 200ms ${ease}, box-shadow 200ms ${ease};
    }
    .sp-row:hover {
      border-color: ${color.hoverBorder};
      box-shadow: 0 6px 20px rgba(10,10,10,0.08);
    }
    .sp-row-main    { flex: 1; min-width: 0; }
    .sp-row-actions { display: flex; align-items: center; gap: 12px; flex-shrink: 0; }
    /* Same hover-grow feel as .stat-view-btn on the dashboard's Students
       Overview card, so the view icon here reacts the same way on hover. */
    .sp-row-action { transition: transform 0.18s ${ease}, filter 0.18s ${ease}; }
    .sp-row-action:hover { transform: scale(1.15); }
    /* Row meta line: wraps gracefully on narrow screens */
    .sp-row-meta {
      display: flex;
      align-items: center;
      gap: 6px;
      margin-top: 3px;
      flex-wrap: wrap;
      row-gap: 2px;
    }
    /* The "View placement" link is redundant on small screens — the whole
       row is tappable, and the space is better spent on the name. */
    @media (max-width: 560px) {
      .sp-row { padding: 12px 14px; gap: 10px; }
      .sp-row-action { display: none; }
    }

    .sp-list-wrapper :focus-visible,
    .sp-modal-inner :focus-visible {
      outline: none;
      box-shadow: ${shadow.focus};
      border-radius: ${radius.pill};
    }
    .sp-modal-inner {
      background: ${surface};
      border: 1px solid ${line};
      border-radius: ${radius.panel};
      width: 660px;
      max-width: calc(100vw - 32px);
      max-height: 80vh;
      display: flex;
      flex-direction: column;
      overflow: hidden;
      box-shadow: ${shadow.panel};
    }

    @media (max-width: 560px) {
      .sp-modal-inner {
        max-width: calc(100vw - 72px);
        max-height: 46vh;
        border-radius: ${radius.card};
      }
    }

    .sp-modal-header {
      padding: 22px 26px 14px;
      display: flex;
      align-items: center;
      justify-content: space-between;
      flex-shrink: 0;
    }
    @media (max-width: 480px) {
      .sp-modal-header { padding: 16px 16px 10px; }
    }

    .sp-modal-body {
      padding: 0 26px 26px;
      overflow-y: auto;
      flex: 1;
      min-height: 0;
    }
    @media (max-width: 480px) {
      .sp-modal-body { padding: 0 16px 18px; }
    }

    .sp-name-pill {
      display: flex;
      align-items: center;
      gap: 14px;
      background: ${lineSoft};
      border: 1px solid ${line};
      border-radius: ${radius.pill};
      padding: 10px 20px 10px 10px;
      margin-bottom: 20px;
    }
    @media (max-width: 400px) {
      .sp-name-pill { padding: 8px 14px 8px 8px; gap: 10px; }
    }

    /* Detail grid in modal: 2-col → 1-col */
    .sp-detail-grid {
      display: grid;
      grid-template-columns: 1fr 1fr;
      gap: 12px;
    }
    @media (max-width: 400px) {
      .sp-detail-grid { grid-template-columns: 1fr; }
    }

    @media (prefers-reduced-motion: reduce) {
      .sp-row { transition: none; }
    }
  `}</style>
);

// Sections run A–Z (26). The Section filter only lists the ones that actually
// have registered students, so the full range never clutters the panel.
const sectionLetterOf = (s) => String(s?.yearSection || "").split("-").pop().trim().toUpperCase();

// Sex options
const SEX_OPTIONS = ["Male", "Female"];

// ── Shared chip style: one look for every selectable pill in this screen ──────
const chip = (on) => ({
  padding: "5px 12px",
  borderRadius: radius.pill,
  fontFamily: font.ui,
  ...type.helper,
  cursor: "pointer",
  userSelect: "none",
  background: on ? panel : color.wine800,
  color: on ? onPanel : inkBody,
  border: `1px solid ${on ? panel : line}`,
  transition: `all 160ms ${ease}`,
});

const StudentAvatar = ({ size = 42, userIcon: themedUserIcon = blackUserIcon }) => (
  <img
    src={themedUserIcon}
    alt=""
    style={{ width: size, height: size, objectFit: "contain", flexShrink: 0 }}
  />
);

// ── Post as applied (read-only) ─────────────────────────────────────────────
// Opens for ANY application, whatever happened to the post: shows the live
// post when it still exists (expired, disabled or closed included — with a
// label, never an Apply button), otherwise the snapshot saved at apply time.
// Replaces window.alert(): same message, but styled like the rest of the app and
// dismissible with OK, Esc, or a click outside.
const NoticeModal = ({ title, message, onClose }) => {
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);
  return (
    <div
      onClick={onClose}
      style={{ position: "fixed", inset: 0, background: "rgba(10,10,10,0.55)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1200, padding: "clamp(16px, 5vw, 24px)" }}
    >
      <div
        role="alertdialog"
        aria-modal="true"
        aria-labelledby="notice-title"
        aria-describedby="notice-message"
        onClick={(e) => e.stopPropagation()}
        style={{ background: color.white, borderRadius: radius.card, width: "100%", maxWidth: "400px", padding: "24px", boxShadow: "0 12px 40px rgba(0,0,0,0.25)" }}
      >
        <h2 id="notice-title" style={{ fontFamily: font.ui, fontSize: "clamp(1.05rem, 4vw, 1.25rem)", fontWeight: 600, color: ink, margin: 0 }}>{title}</h2>
        <p id="notice-message" style={{ fontFamily: font.ui, ...type.helper, color: inkBody, margin: "10px 0 20px", lineHeight: 1.5 }}>{message}</p>
        <div style={{ display: "flex", justifyContent: "flex-end" }}>
          <button
            autoFocus
            onClick={onClose}
            style={{ background: ink, color: color.white, border: "none", borderRadius: radius.pill, padding: "9px 26px", cursor: "pointer", fontFamily: font.ui, ...type.control }}
          >
            OK
          </button>
        </div>
      </div>
    </div>
  );
};

const PostRecordModal = ({ app, livePost, company, onClose }) => {
  // livePost: undefined = still loading, null = post no longer exists.
  const closure = livePost === undefined ? null : getPostClosure(app.postId, { [app.postId]: livePost });
  const usingSnapshot = !livePost && !!app.postSnapshot;
  const post = livePost || app.postSnapshot || null;
  const label = livePost === undefined && !app.postSnapshot
    ? "Loading post…"
    : !livePost && !app.postSnapshot
    ? "This post is no longer available and no copy was saved (applied before snapshots existed)."
    : usingSnapshot
      ? (livePost === null ? "The company removed this post. Showing the copy saved when the student applied." : "Showing the copy saved when the student applied.")
      : closure
        ? `${closure.detail} Shown for review only.`
        : livePost?.disabled
          ? "The company has disabled this post. Shown for review only."
          : "";
  const lines = (v) => String(v || "").split(/\n+/).map(x => x.trim()).filter(Boolean);
  const Section = ({ title, value }) => {
    const items = Array.isArray(value) ? value.filter(Boolean) : lines(value);
    if (items.length === 0) return null;
    return (
      <div style={{ marginTop: "14px" }}>
        <p style={{ fontFamily: font.ui, ...type.label, color: ink, marginBottom: "4px" }}>{title}</p>
        {items.map((t, i) => <p key={i} style={{ fontFamily: font.ui, ...type.helper, color: inkBody, lineHeight: 1.5, margin: "2px 0" }}>{t}</p>)}
      </div>
    );
  };
  const address = post?.postLocation?.address || post?.address || "";
  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(10,10,10,0.55)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1100, padding: "clamp(16px, 5vw, 24px)" }}>
      <div className="sp-modal-inner">
        <div className="sp-modal-header">
          <h2 style={{ fontFamily: font.ui, fontSize: "clamp(1.05rem, 4vw, 1.25rem)", fontWeight: 600, color: ink }}>Post as applied</h2>
          <button onClick={onClose} aria-label="Close" style={{ background: lineSoft, border: `1px solid ${line}`, borderRadius: "50%", width: "30px", height: "30px", color: inkMuted, cursor: "pointer", flexShrink: 0 }}>✕</button>
        </div>
        <div className="sp-modal-body">
          {label && (
            <p style={{ fontFamily: font.ui, fontSize: "0.78rem", color: "#444", background: "#F2F2F2", border: "1px solid #E0E0E0", borderRadius: "10px", padding: "8px 12px", margin: "0 0 12px", lineHeight: 1.45 }}>{label}</p>
          )}
          <p style={{ fontFamily: font.ui, fontSize: "1.05rem", fontWeight: 600, color: ink, margin: 0 }}>
            {post?.companyName || company?.name || app.companyName || "Unknown company"}
          </p>
          <p style={{ fontFamily: font.ui, ...type.helper, color: inkMuted, margin: "2px 0 0" }}>
            {[post?.industry || company?.industry, address].filter(Boolean).join(" · ") || "—"}
          </p>
          {post && (
            <>
              <Section title="Description" value={post.description} />
              <Section title="Requirements" value={post.requirements} />
              <Section title="Skills required" value={post.skillsRequired} />
              <Section title="Benefits" value={post.benefits} />
              <Section title="Working hours" value={post.workingHoursList} />
              <Section title="Programs and slots" value={(post.courseSelections || []).map(s => `${[s.college, s.program].filter(Boolean).join(" — ")}${s.slot ? ` · ${s.slot} slot${s.slot > 1 ? "s" : ""}` : ""}`)} />
              <Section title="Contact" value={[post.contactEmail, post.phone && post.phone.trim() !== "+63" ? post.phone : ""].filter(Boolean)} />
              {post.expirationDate && <Section title="Expiration" value={formatPostDate(post.expirationDate)} />}
            </>
          )}
          {/* Company details straight from the company's own profile, so the
              coordinator can review the company even when it's no longer in
              their active Company List (e.g. a withdrawn program). */}
          {company && (
            <div style={{ marginTop: "18px", padding: "12px 14px", background: color.wine800, border: `1px solid ${line}`, borderRadius: radius.card }}>
              <p style={{ fontFamily: font.ui, ...type.label, color: ink, marginBottom: "4px" }}>Company</p>
              {[
                ["Name", company.companyName || company.name],
                ["Industry", Array.isArray(company.industry) ? company.industry.join(", ") : company.industry],
                ["Email", company.email],
                ["Location", [company.location?.street, company.location?.barangay, company.location?.city, company.location?.province].filter(Boolean).join(", ")],
                ["Account", company.status],
              ].filter(([, v]) => v).map(([k, v]) => (
                <p key={k} style={{ fontFamily: font.ui, ...type.helper, color: inkBody, margin: "2px 0" }}><span style={{ fontWeight: 600 }}>{k}:</span> {v}</p>
              ))}
              {Array.isArray(company.verificationDocs) && company.verificationDocs.length > 0 && (
                <p style={{ fontFamily: font.ui, ...type.helper, color: inkBody, margin: "6px 0 0" }}>
                  <span style={{ fontWeight: 600 }}>Verification documents:</span>{" "}
                  {company.verificationDocs.map((url, i) => (
                    <a key={i} href={url} target="_blank" rel="noopener noreferrer" style={{ color: ink, marginRight: "8px" }}>Document {i + 1}</a>
                  ))}
                </p>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const PlacementModal = ({ student, onClose, onNavigateToCompany, companies, onMessageStudent, userIcon: themedUserIcon = blackUserIcon, postsById = {} }) => {
  // companyId -> standing, from the companies this modal already receives.
  const companyStandings = React.useMemo(() => standingsByCompany(companies), [companies]);
  const [applications, setApplications] = useState([]);
  const [viewingPostApp, setViewingPostApp] = useState(null);
  const [reviewingId, setReviewingId] = useState(null);

  // Coordinator confirms they reviewed an accepted placement (clears ⚑).
  const markReviewed = async (app) => {
    const appId = app.id;
    setReviewingId(appId);
    try {
      await updateDoc(doc(db, "applications", appId), {
        coordinatorReviewedAt: serverTimestamp(),
        coordinatorReviewedBy: auth.currentUser?.uid || "",
      });
    } catch (err) {
      console.error("Failed to mark placement as reviewed:", err);
      setReviewingId(null);
      return;
    }
    // Let the student know their coordinator has seen the placement.
    // Best-effort: the review above is already saved.
    if (app.studentId) {
      const companyName = companies.find(c => c.id === app.companyId)?.name || app.companyName || "the company";
      addDoc(collection(db, "notifications"), {
        studentId:     app.studentId,
        type:          "placement_reviewed",
        applicationId: appId,
        companyId:     app.companyId || "",
        companyName,
        message:       `Your OJT coordinator reviewed your placement at ${companyName}.`,
        read:          false,
        createdAt:     serverTimestamp(),
      }).catch(err => console.error("Placement reviewed, but notifying the student failed:", err));
    }
    setReviewingId(null);
  };

  useEffect(() => {
    if (!student?.id) return;
    const q = query(collection(db, "applications"), where("studentId", "==", student.id));
    // Live, so a withdrawal (by the company or the student) shows up while open.
    const unsub = onSnapshot(q, snap => {
      const list = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      // Follow-up items first, then newest.
      list.sort((a, b) =>
        (followUpReason(b, list, companyStandings) ? 1 : 0) - (followUpReason(a, list, companyStandings) ? 1 : 0) ||
        (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0));
      setApplications(list);
    }, err => console.error("Failed to load student applications:", err));
    return () => unsub();
  }, [student?.id]);

  const fullName = getFullName(student);

  const handleVisitCompany = (companyId) => {
    if (!companyId) return;
    onClose();
    onNavigateToCompany(companyId, student.id);
  };

  const handleMessage = () => {
    onClose();
    onMessageStudent?.({ id: student.id, name: fullName });
  };

  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(10,10,10,0.5)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000, padding: "clamp(16px, 5vw, 24px)" }}>
      {viewingPostApp && (
        <PostRecordModal
          app={viewingPostApp}
          livePost={viewingPostApp.postId && viewingPostApp.postId in postsById ? (postsById[viewingPostApp.postId] || null) : undefined}
          company={companies.find(c => c.id === viewingPostApp.companyId)}
          onClose={() => setViewingPostApp(null)}
        />
      )}
      <div id="sl-modal-container" className="sp-modal-inner">
        <div className="sp-modal-header">
          <h2 style={{ fontFamily: font.ui, fontSize: "clamp(1.125rem, 4vw, 1.375rem)", fontWeight: 600, letterSpacing: "-0.01em", color: ink }}>Placement</h2>
          <button onClick={onClose} aria-label="Close" style={{ background: lineSoft, border: `1px solid ${line}`, borderRadius: "50%", width: "30px", height: "30px", color: inkMuted, fontSize: "0.9rem", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>✕</button>
        </div>

        <div className="sp-modal-body">
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "10px" }}>
            <div id="sl-modal-name" className="sp-name-pill" style={{ flex: 1, minWidth: 0 }}>
              <StudentAvatar size={45} userIcon={themedUserIcon} />
              <span style={{ fontFamily: font.ui, fontSize: "clamp(0.95rem, 4vw, 1.0625rem)", fontWeight: 600, letterSpacing: "-0.01em", color: ink, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{fullName}</span>
            </div>
            <button
              id="sl-modal-message-btn"
              onClick={handleMessage}
              title={`Message ${fullName}`}
              style={{
                display: "flex", alignItems: "center", gap: "7px", flexShrink: 0,
                background: panel, color: onPanel, border: "none", borderRadius: radius.pill,
                padding: "10px 18px", cursor: "pointer", marginBottom: "20px",
                fontFamily: font.ui, ...type.control,
                transition: `background 220ms ${ease}`,
              }}
              onMouseEnter={e => (e.currentTarget.style.background = panelDeep)}
              onMouseLeave={e => (e.currentTarget.style.background = panel)}
            >
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
              </svg>
              Message
            </button>
          </div>

          {applications.length === 0 ? (
            <div id="sl-modal-applications" style={{ padding: `${space.lg} ${space.md}`, textAlign: "center", background: color.wine800, border: `1px dashed ${color.wine400}`, borderRadius: radius.card }}>
              <p style={{ fontFamily: font.ui, ...type.body, color: inkBody }}>No applications yet</p>
              <p style={{ fontFamily: font.ui, ...type.helper, color: inkMuted, marginTop: "2px" }}>Applications appear here once this student applies to a company.</p>
            </div>
          ) : (
            <div id="sl-modal-applications">
              <p style={{ fontFamily: font.ui, ...type.label, color: ink, marginBottom: space.sm }}>Applications</p>
              <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
                {applications.map(app => {
                  const appCompany = companies.find(c => c.id === app.companyId);
                  const sc = STATUS_COLORS[app.status] || { bg: color.wine400, color: ink };
                  const reason = followUpReason(app, applications, companyStandings);
                  const notices = [];
                  // Company standing first: it explains why an application has
                  // gone quiet, which the other notices don't.
                  const appStanding = companyStandings[app.companyId];
                  if (appStanding && !appStanding.isActive && !CLOSED_STATUSES.includes(app.status)) {
                    notices.push(appStanding.message);
                  }
                  const closure = getPostClosure(app.postId, postsById);
                  if (closure && app.status !== "Withdrawn") {
                    notices.push(`${closure.detail}${["Pending", "In Review", "To Interview"].includes(app.status) ? " Application still open with the company." : ""}`);
                  }
                  if (app.affiliationWithdrawn) {
                    notices.push(`Company is no longer in the program${app.affiliationWithdrawnLabel ? ` (${app.affiliationWithdrawnLabel})` : ""}${app.affiliationWithdrawnAt?.seconds ? ` on ${new Date(app.affiliationWithdrawnAt.seconds * 1000).toLocaleDateString("en-US", { month: "short", day: "2-digit", year: "numeric" })}` : ""}`);
                  }
                  if (!app.affiliationWithdrawn && app.affiliationRestoredAt?.seconds) {
                    notices.push(`Company is no longer in the program${app.affiliationWithdrawnAt?.seconds ? ` on ${new Date(app.affiliationWithdrawnAt.seconds * 1000).toLocaleDateString("en-US", { month: "short", day: "2-digit", year: "numeric" })}` : ""} and restored on ${new Date(app.affiliationRestoredAt.seconds * 1000).toLocaleDateString("en-US", { month: "short", day: "2-digit", year: "numeric" })}`);
                  }
                  if (app.status === "Accepted") {
                    const confirmedElsewhere = !app.placementConfirmed && confirmedOf(applications);
                    notices.push(app.placementConfirmed
                      ? `✓ Confirmed by the student as their OJT placement${app.placementConfirmedAt?.seconds ? ` on ${new Date(app.placementConfirmedAt.seconds * 1000).toLocaleDateString("en-US", { month: "short", day: "2-digit", year: "numeric" })}` : ""}`
                      : confirmedElsewhere
                        ? `Accepted — student already has a confirmed placement at ${companies.find(c => c.id === confirmedElsewhere.companyId)?.name || confirmedElsewhere.companyName || "another company"}`
                        : "Accepted — waiting for the student to confirm it as their OJT placement");
                  }
                  if (app.status === "Withdrawn") {
                    notices.push(`Withdrawn by the student${app.withdrawnAt?.seconds ? ` on ${new Date(app.withdrawnAt.seconds * 1000).toLocaleDateString("en-US", { month: "short", day: "2-digit", year: "numeric" })}` : ""}${app.statusBeforeWithdrawal ? ` — was ${app.statusBeforeWithdrawal}` : ""}`);
                    if (app.withdrawalReason) notices.push(`Reason: ${[app.withdrawalReason, app.withdrawalReasonDetails].filter(Boolean).join(" — ")}`);
                  }
                  return (
                    <div key={app.id} style={{ background: color.wine800, border: `1px solid ${reason ? "#F0D48A" : line}`, borderRadius: radius.card, padding: "10px 14px" }}>
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "10px" }}>
                      <span style={{ fontFamily: font.ui, ...type.helper, color: inkBody, fontWeight: 500, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                        {/* The company name saved on the application keeps the record
                            readable even if the company is no longer in this list. */}
                        {appCompany?.name || app.companyName || "Unknown company"}
                      </span>
                      <div style={{ display: "flex", alignItems: "center", gap: "12px", flexShrink: 0 }}>
                        <span style={{ background: sc.bg, color: sc.color, borderRadius: radius.pill, padding: "3px 11px", fontFamily: font.ui, fontSize: "0.75rem", fontWeight: 500 }}>
                          {app.status}
                        </span>
                        {/* Always available — works for expired, closed or deleted posts. */}
                        <span
                          onClick={() => setViewingPostApp(app)}
                          style={{ fontFamily: font.ui, ...type.helper, fontWeight: 500, color: ink, cursor: "pointer", whiteSpace: "nowrap", textDecoration: "underline", textUnderlineOffset: "3px" }}
                        >
                          View post
                        </span>
                      </div>
                    </div>
                    {notices.length > 0 && (
                      <div style={{ margin: "6px 0 0", display: "flex", flexDirection: "column", gap: "2px" }}>
                        {notices.map((n, i) => (
                          <p key={i} style={{ fontFamily: font.ui, fontSize: "0.72rem", color: inkMuted, margin: 0, lineHeight: 1.4 }}>
                            {n}
                          </p>
                        ))}
                      </div>
                    )}
                    {reason && (
                      <p style={{ fontFamily: font.ui, fontSize: "0.72rem", fontWeight: 600, color: "#8a5a00", background: "#FFF3D6", border: "1px solid #F0D48A", borderRadius: "8px", padding: "4px 8px", margin: "6px 0 0", display: "inline-block" }}>
                        ⚑ Follow-up needed: {reason}
                      </p>
                    )}
                    {needsPlacementReview(app) && (
                      <div style={{ display: "flex", gap: "8px", flexWrap: "wrap", marginTop: "8px" }}>
                        <button
                          onClick={() => setViewingPostApp(app)}
                          style={{ background: color.white, color: ink, border: `1px solid ${line}`, borderRadius: radius.pill, padding: "5px 14px", cursor: "pointer", fontFamily: font.ui, fontSize: "0.75rem", fontWeight: 600 }}
                        >
                          Review post
                        </button>
                        <button
                          onClick={() => markReviewed(app)}
                          disabled={reviewingId === app.id}
                          style={{ background: panel, color: onPanel, border: "none", borderRadius: radius.pill, padding: "5px 14px", cursor: reviewingId === app.id ? "not-allowed" : "pointer", opacity: reviewingId === app.id ? 0.7 : 1, fontFamily: font.ui, fontSize: "0.75rem", fontWeight: 600 }}
                        >
                          {reviewingId === app.id ? "Saving…" : "Mark as reviewed"}
                        </button>
                      </div>
                    )}
                    {app.status === "Accepted" && app.coordinatorReviewedAt?.seconds && (
                      <p style={{ fontFamily: font.ui, fontSize: "0.72rem", color: "#2a7a2a", margin: "6px 0 0" }}>
                        ✓ Placement reviewed on {new Date(app.coordinatorReviewedAt.seconds * 1000).toLocaleDateString("en-US", { month: "short", day: "2-digit", year: "numeric" })}
                      </p>
                    )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          <div id="sl-modal-details" style={{ marginTop: space.lg, padding: "14px 16px", background: color.wine800, borderRadius: radius.card, border: `1px solid ${line}` }}>
            <div className="sp-detail-grid">
              {[
                { label: "Student ID",     value: student.studentId },
                { label: "Sex",            value: student.sex },
                { label: "College",        value: student.college,        full: true },
                { label: "Program",        value: student.program,        full: true },
                { label: "Year & section", value: student.yearSection },
              ].map(({ label, value, full }) => (
                <div key={label} style={{ gridColumn: full ? "1 / -1" : "auto" }}>
                  <p style={{ fontFamily: font.ui, ...type.helper, color: inkMuted, marginBottom: "2px" }}>{label}</p>
                  <p style={{ fontFamily: font.ui, ...type.label, color: ink }}>{value}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// ── Search: first name, middle initial, last name, suffix and student ID only ──
// Every word typed must match one of those fields, so "juan dela cruz", "cruz jr"
// and "m. santos" all work. A single letter matches a middle initial or the start
// of a name/ID, so typing "m" doesn't match every name that merely contains an m.
const searchKey = (v) => String(v ?? "").toLowerCase().replace(/[.,]/g, "").trim();
const matchesStudentSearch = (s, query) => {
  const tokens = searchKey(query).split(/\s+/).filter(Boolean);
  if (tokens.length === 0) return true;
  const names = [s.firstName, s.lastName, s.suffix].map(searchKey).filter(Boolean);
  const mi = searchKey(s.middleInitial);
  const id = searchKey(s.studentId);
  return tokens.every(t => (t.length === 1
    ? (mi === t || names.some(n => n.startsWith(t)) || id.startsWith(t))
    : (names.some(n => n.includes(t)) || id.includes(t) || mi === t)));
};

// ── Program / Major options for the filter panel ──
// The department itself is fixed (the list is already limited to the coordinator's
// own department(s)), so it is never a filter. Only programs are listed here.
const programOptionsFor = (colleges, departments, departmentNames) => {
  const deps = departments || {};
  const names = departmentNames || [];
  const resolve = (c) => (deps[c] ? c : names.find(n => deps[n]?.abbr === c) || null);
  const scope = [...new Set((colleges || []).map(resolve).filter(Boolean))];
  return (scope.length > 0 ? scope : names).flatMap(dept =>
    (deps[dept]?.programs || []).map(p => ({ dept, name: p.name, specializations: p.specializations || [] })));
};

const FilterPanel = ({ filters, setFilters, filterRef, coordinatorColleges = [], batchOptions = [], hasUnbatched = false, sectionOptions = [] }) => {
  const { departments, departmentNames } = useDepartmentsPrograms();
  // Department is fixed (the coordinator's own), so only Program / Major are filterable,
  // and only when there is something to choose between.
  const programOptions  = programOptionsFor(coordinatorColleges, departments, departmentNames);
  const showDeptLabels  = new Set(programOptions.map(p => p.dept)).size > 1;
  const showPrograms    = programOptions.length > 1;
  const activeProgram   = filters.program ? programOptions.find(p => p.name === filters.program)
                                          : (programOptions.length === 1 ? programOptions[0] : null);
  const majors          = activeProgram?.specializations || [];
  const showMajors      = majors.length > 0;

  const clearAll = () => {
    // Everything in this panel, batch included. (The status chips above the
    // list are a separate control and keep their own selection.)
    setFilters(prev => ({ ...prev, college: "", program: "", specialization: "", sex: "", section: "", batch: "" }));
  };

  const toggleSex     = (val) => setFilters(prev => ({ ...prev, sex:     prev.sex     === val ? "" : val }));
  const toggleSection = (val) => setFilters(prev => ({ ...prev, section: prev.section === val ? "" : val }));
  const toggleProgram = (prog) => setFilters(prev => ({ ...prev, program: prev.program === prog ? "" : prog, specialization: "" }));
  const toggleSpec    = (spec) => setFilters(prev => ({ ...prev, specialization: prev.specialization === spec ? "" : spec }));
  const toggleBatch   = (val)  => setFilters(prev => ({ ...prev, batch: prev.batch === val ? "" : val }));

  const groupLabel = { fontFamily: font.ui, ...type.label, color: ink };
  const emptyNote  = { fontFamily: font.ui, ...type.helper, color: inkFaint };

  return (
    <div
      ref={filterRef}
      style={{
        position: "absolute", top: "48px", right: 0, width: "266px",
        background: surface, border: `1px solid ${line}`, borderRadius: radius.card,
        boxShadow: shadow.panel, zIndex: 100, overflowX: "hidden", overflowY: "auto",
        maxHeight: "min(72vh, 560px)",
        fontFamily: font.ui,
      }}
    >
      {/* Sex + Clear all */}
      <div style={{ padding: "12px 14px 6px" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: space.sm }}>
          <p style={groupLabel}>Sex</p>
          <button onClick={clearAll} style={{ background: "none", border: "none", fontFamily: font.ui, ...type.helper, color: inkMuted, cursor: "pointer", padding: 0, textDecoration: "underline" }}>Clear all</button>
        </div>
        <div style={{ display: "flex", flexWrap: "wrap", gap: "6px" }}>
          {SEX_OPTIONS.length > 0 ? (
            SEX_OPTIONS.map(s => (
              <span key={s} onClick={() => toggleSex(s)} style={chip(filters.sex === s)}>{s}</span>
            ))
          ) : (
            <span style={emptyNote}>No options available</span>
          )}
        </div>
      </div>

      <hr style={{ border: "none", borderTop: `1px solid ${lineSoft}`, margin: "10px 0" }} />

      {/* Batch — academic year, built from the students actually loaded */}
      <div style={{ padding: "0 14px 12px" }}>
        <p style={{ ...groupLabel, marginBottom: space.sm }}>Batch</p>
        <div style={{ display: "flex", flexWrap: "wrap", gap: "6px" }}>
          {batchOptions.length > 0 || hasUnbatched ? (
            <>
              {batchOptions.map(b => (<span key={b} onClick={() => toggleBatch(b)} style={chip(filters.batch === b)}>{b}</span>))}
              {hasUnbatched && (<span onClick={() => toggleBatch(BATCH_NONE)} style={chip(filters.batch === BATCH_NONE)}>No batch</span>)}
            </>
          ) : (
            <span style={emptyNote}>No batches yet</span>
          )}
        </div>
      </div>

      <hr style={{ border: "none", borderTop: `1px solid ${lineSoft}`, margin: "10px 0" }} />

      {/* Section */}
      <div style={{ padding: "0 14px 12px" }}>
        <p style={{ ...groupLabel, marginBottom: space.sm }}>Section</p>
        <div style={{ display: "flex", flexWrap: "wrap", gap: "6px" }}>
          {sectionOptions.length > 0 ? (
            sectionOptions.map(o => (
              <span key={o.key} onClick={() => toggleSection(o.key)} style={chip(filters.section === o.key)}>
                {o.label}<span style={{ opacity: 0.6, marginLeft: "4px" }}>{o.count}</span>
              </span>
            ))
          ) : (
            <span style={emptyNote}>No students registered in any section yet</span>
          )}
        </div>
      </div>

      {/* Program and Major. Department is fixed, so there is no Department filter. This
          whole section is hidden when the department has no other program or major to pick. */}
      {(showPrograms || showMajors) && (
        <>
          <hr style={{ border: "none", borderTop: `1px solid ${lineSoft}`, margin: 0 }} />
          <div style={{ padding: "12px 14px 14px" }}>
            {showPrograms && (
              <div style={{ marginBottom: showMajors ? "12px" : 0 }}>
                <p style={{ ...groupLabel, marginBottom: space.sm }}>Program</p>
                <div style={{ maxHeight: "170px", overflowY: "auto", display: "flex", flexDirection: "column", gap: "8px" }}>
                  {[...new Set(programOptions.map(p => p.dept))].map(dept => (
                    <div key={dept}>
                      {showDeptLabels && <p style={{ ...emptyNote, margin: "0 0 4px" }}>{dept}</p>}
                      <div style={{ display: "flex", flexWrap: "wrap", gap: "6px" }}>
                        {programOptions.filter(p => p.dept === dept).map(p => (
                          <span key={p.name} onClick={() => toggleProgram(p.name)} style={chip(filters.program === p.name)}>{p.name}</span>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
            {showMajors && (
              <div>
                <p style={{ ...groupLabel, marginBottom: space.sm }}>Major</p>
                <div style={{ maxHeight: "150px", overflowY: "auto", display: "flex", flexWrap: "wrap", gap: "6px" }}>
                  {majors.map(m => (
                    <span key={m} onClick={() => toggleSpec(m)} style={chip(filters.specialization === m)}>{m}</span>
                  ))}
                </div>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};

// ── College name variants ────────────────────────────────────────
// Student docs are not consistent about `college`: older ones hold the short
// code ("CCS"), newer ones the full name ("College of Computer Studies"). And
// `coordinatorColleges` itself may arrive as either, depending on what the
// parent computed from the coordinator's deptSelections. Querying one form
// alone silently hides every student saved in the other — which is exactly how
// this screen and the Students Account screen ended up showing different
// people. So match on both forms until the data is cleaned up.
// Firestore allows up to 10 values in an "in" clause, hence the slice.
export const useCollegeVariants = (coordinatorColleges) => {
  const { departments, departmentNames } = useDepartmentsPrograms();
  return React.useMemo(() => {
    const abbrToFull = {}, fullToAbbr = {};
    departmentNames.forEach(name => {
      const abbr = departments[name]?.abbr;
      if (abbr) { abbrToFull[abbr] = name; fullToAbbr[name] = abbr; }
    });
    const out = new Set();
    (coordinatorColleges || []).forEach(c => {
      if (!c) return;
      out.add(c);
      if (abbrToFull[c]) out.add(abbrToFull[c]);
      if (fullToAbbr[c]) out.add(fullToAbbr[c]);
    });
    return [...out].slice(0, 10);
  }, [coordinatorColleges, departments, departmentNames]);
};

// ── Pagination: 10 rows per page ──────────────────────────────────────────────
const PAGE_SIZE = 10;
// The header (title, search, filters) is fixed; only the list under it scrolls, with its own scrollbar
// (same idea as Account Profile in Admin). The list box reaches into the screen padding so its scrollbar sits at the edge.
const PIN_PAD = "clamp(16px, 4vw, 28px)";
const PIN_PAD_X = "clamp(16px, 4vw, 32px)";
const fixedHead = { flexShrink: 0, position: "relative", zIndex: 30 };
const listScroll = { flex: "1 1 auto", minHeight: 0, overflowY: "auto", margin: `0 calc(-1 * ${PIN_PAD_X}) calc(-1 * ${PIN_PAD})`, padding: `0 ${PIN_PAD_X} ${PIN_PAD}` };
// Next/previous page: the list goes back to its start.
const scrollListToTop = (scroller) => { if (scroller) scroller.scrollTo({ top: 0, behavior: "smooth" }); };
// « ‹ 1 2 3 … 9 › » — same look as the Admin screens: current-page badge + page buttons in one rounded bar.
const PaginationBar = ({ page: current, totalPages, onPage, from, to, total }) => {
  const range = (a, b) => Array.from({ length: b - a + 1 }, (_, i) => a + i);
  const items = totalPages <= 7 ? range(1, totalPages)
    : current <= 4 ? [...range(1, 5), "gap-end", totalPages]
    : current >= totalPages - 3 ? [1, "gap-start", ...range(totalPages - 4, totalPages)]
    : [1, "gap-start", current - 1, current, current + 1, "gap-end", totalPages];
  const btn = (on, disabled) => ({
    minWidth: "36px", height: "36px", padding: "0 10px", borderRadius: "10px",
    border: `1.5px solid ${on ? color.blush50 : color.wine700}`, background: on ? color.blush50 : color.white, color: on ? color.white : color.ink,
    fontFamily: font.ui, fontSize: "0.82rem", fontWeight: 600, cursor: disabled ? "default" : "pointer", opacity: disabled ? 0.45 : 1,
  });
  return (
    <div style={{ display: "flex", flexWrap: "wrap-reverse", alignItems: "center", justifyContent: total != null ? "space-between" : "center", gap: "12px", padding: "20px 0 4px" }}>
      {total != null && <span style={{ fontFamily: font.ui, fontSize: "0.8rem", color: color.inkMuted }}>Showing {from}–{to} of {total}</span>}
      <nav aria-label="Pagination" style={{ marginLeft: "auto", display: "inline-flex", alignItems: "stretch", maxWidth: "100%", background: color.white, border: `1px solid ${color.wine700}`, borderRadius: "16px", boxShadow: "0 4px 16px rgba(20,20,20,0.06)", overflow: "hidden" }}>
        <div title="Current page" style={{ display: "flex", alignItems: "center", justifyContent: "center", minWidth: "52px", padding: "0 14px", background: color.wine800, borderRight: `1px solid ${color.wine700}`, fontFamily: font.ui, fontSize: "0.9rem", fontWeight: 700, color: color.ink }}>
          {String(current).padStart(2, "0")}
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: "6px", padding: "10px 12px", flexWrap: "wrap", justifyContent: "center" }}>
          <button type="button" aria-label="First page" title="First page" style={btn(false, current === 1)} disabled={current === 1} onClick={() => onPage(1)}>«</button>
          <button type="button" aria-label="Previous page" title="Previous page" style={btn(false, current === 1)} disabled={current === 1} onClick={() => onPage(current - 1)}>‹</button>
          {items.map((it) => (typeof it === "number"
            ? <button key={it} type="button" style={btn(it === current, false)} aria-current={it === current ? "page" : undefined} onClick={() => onPage(it)}>{it}</button>
            : <span key={it} style={{ color: color.inkMuted, padding: "0 4px" }}>…</span>))}
          <button type="button" aria-label="Next page" title="Next page" style={btn(false, current === totalPages)} disabled={current === totalPages} onClick={() => onPage(current + 1)}>›</button>
          <button type="button" aria-label="Last page" title="Last page" style={btn(false, current === totalPages)} disabled={current === totalPages} onClick={() => onPage(totalPages)}>»</button>
        </div>
      </nav>
    </div>
  );
};

const CoordinatorStudentListScreen = ({ coordinatorColleges, onNavigateToCompany, onMessageStudent, initialViewingStudentId, onClearInitialViewingStudent, userIcon: themedUserIcon = blackUserIcon, viewIcon: themedViewIcon = blackViewIcon, onViewingStudentChange, initialStatusFilter = "" }) => {
  const [search, setSearch]                 = useState("");
  const [curPage, setCurPage]                 = useState(1);
  // Next/previous page: the list goes back to its start.
  const scrollRef = useRef(null);
  const goToPage = (n) => { setCurPage(n); requestAnimationFrame(() => scrollListToTop(scrollRef.current)); };
  const [viewingStudent, setViewingStudent] = useState(null);
  // Lets the dashboard's "?" help button and auto-tour switch to
  // HELP_STEPS_BY_NAV.studentlistmodal while a student's Placement modal is
  // open — same pattern as Students Account's onViewingStudentChange.
  useEffect(() => { onViewingStudentChange?.(!!viewingStudent); }, [viewingStudent, onViewingStudentChange]);
  const [showFilter, setShowFilter]         = useState(false);
  const [showExport, setShowExport]         = useState(false);
  const [exportingPdf, setExportingPdf]     = useState(false);
  // { title, message } while the export notice modal is open, else null.
  const [notice, setNotice]                 = useState(null);
  // Which batch the export covers. "" = every batch (grouped by batch in the PDF).
  // Seeded from the on-screen batch filter each time the Export menu opens.
  const [exportBatch, setExportBatch]       = useState("");
  const [filters, setFilters]               = useState({ college: "", program: "", specialization: "", sex: "", section: "", status: initialStatusFilter || "", batch: "" });
  // A dashboard notification can open this list pre-filtered (e.g. "Needs Follow-up").
  useEffect(() => {
    if (initialStatusFilter) setFilters(p => ({ ...p, status: initialStatusFilter }));
  }, [initialStatusFilter]);

  const filterRef = useRef(null);
  const exportRef = useRef(null);
  const [students, setStudents]     = useState([]);
  const [companies, setCompanies]   = useState([]);
  // companyId -> standing (suspended / blocked), from the companies above.
  const companyStandings = React.useMemo(() => standingsByCompany(companies), [companies]);
  const [loadingStudents, setLoadingStudents] = useState(true);
  const [applicationsByStudent, setApplicationsByStudent] = useState({});
  // Live post status (expired / closed / removed) behind every application.
  const postsById = usePostsByIds(Object.values(applicationsByStudent).flat().map(a => a.postId));

  const collegeVariants = useCollegeVariants(coordinatorColleges);
  // FIX: `collegeVariants` can be a fresh array reference on every render
  // even when its contents are unchanged (it comes from a useMemo chain fed
  // by useDepartmentsPrograms(), whose own return values aren't guaranteed
  // stable across renders). Using the array itself as a useEffect dependency
  // below made that effect re-fire on every render — and setStudents([])
  // inside it is always a brand-new [] reference, which always triggers
  // another render — producing an infinite loop ("Maximum update depth
  // exceeded"), the same bug fixed on the Student Accounts screen. Deriving
  // a stable, content-based string key instead means the effect only
  // re-runs when the actual list of colleges changes.
  const collegeVariantsKey = collegeVariants.join("|");

  // If we got here because the coordinator pressed "back" on a company
  // profile they reached via a student's Placement modal, reopen that
  // same student's modal instead of dropping them on a bare list.
  useEffect(() => {
    if (!initialViewingStudentId || loadingStudents || students.length === 0) return;
    const match = students.find(s => s.id === initialViewingStudentId);
    if (match) setViewingStudent(match);
    onClearInitialViewingStudent && onClearInitialViewingStudent();
  }, [initialViewingStudentId, loadingStudents, students]);

  // ── Load students — scoped to this coordinator's own department(s). ──────
  // No orderBy in the query on purpose: Firestore drops any document that is
  // missing the ordered field, so orderBy("createdAt") silently hid every
  // student saved without one. Sorting below keeps them in the list — those
  // without a createdAt just sink to the bottom. Dropping the orderBy also
  // means this no longer needs a composite index.
  useEffect(() => {
    if (collegeVariants.length === 0) {
      console.warn("[StudentList] No colleges assigned to this coordinator — nothing to load.", coordinatorColleges);
      setStudents(prev => (prev.length === 0 ? prev : []));
      setLoadingStudents(false);
      return;
    }
    const q = query(
      collection(db, "students"),
      where("college", "in", collegeVariants)
    );
    const unsub = onSnapshot(q, snap => {
      const rows = snap.docs
        .map(d => ({ id: d.id, ...d.data() }))
        // Archived students (Student Accounts → Archive) are no longer current
        // students, so they drop out of this list — their Firestore doc,
        // applications and placement history are all still intact, and
        // restoring them there brings them straight back here.
        // A missing isArchived field means active, which keeps every older
        // student record visible.
        .filter(r => r.isArchived !== true)
        // Alphabetical (last name, first name). Everything downstream — the
        // on-screen list, the batch groups, and the PDF export — keeps this
        // order, because filtering never reorders.
        .sort(compareStudentNames);
      setStudents(rows);
      setLoadingStudents(false);

      // 🔍 TEMP DEBUG — remove once the duplicate issue is confirmed/resolved.
      // Logs any studentId (the school ID number, e.g. "201147323") that
      // appears on more than one Firestore document. If Michael shows up
      // here with 2+ doc IDs, that confirms a duplicate student record.
      const byStudentId = {};
      rows.forEach(r => {
        if (!r.studentId) return;
        (byStudentId[r.studentId] ||= []).push(r.id);
      });
      Object.entries(byStudentId).forEach(([sid, docIds]) => {
        if (docIds.length > 1) {
          console.warn(`[DUPLICATE STUDENT] studentId ${sid} has ${docIds.length} Firestore docs:`, docIds);
        }
      });
    }, err => {
      // Was a bare `() => setLoadingStudents(false)`, which made a failed query
      // look identical to an empty department.
      console.error("[StudentList] Failed to load students:", err);
      setLoadingStudents(false);
    });
    return () => unsub();
  }, [collegeVariantsKey]);

  // ── Load companies ─────────────────────────────────────────────────────────
  useEffect(() => {
    const unsub = onSnapshot(collection(db, "companies"), snap => {
      setCompanies(snap.docs.map(d => ({ id: d.id, name: d.data().companyName || "", ...d.data() })));
    });
    return () => unsub();
  }, []);

  useEffect(() => {
    const q = query(collection(db, "applications"));
    const unsub = onSnapshot(q, snap => {
      const byStudent = {};
      snap.docs.forEach(d => {
        const data = d.data();
        if (!data.studentId) return;
        (byStudent[data.studentId] ||= []).push({ id: d.id, ...data });
      });
      setApplicationsByStudent(byStudent);
    });
    return () => unsub();
  }, []);


  useEffect(() => {
    const handler = (e) => {
      if (filterRef.current && !filterRef.current.contains(e.target)) setShowFilter(false);
      if (exportRef.current && !exportRef.current.contains(e.target)) setShowExport(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  // The status bar has its own row of chips, so the funnel dot only reflects
  // the panel's own filters (sex, section, department).
  const panelFilterKeys = ["college", "program", "specialization", "sex", "section"];
  const hasFilter = panelFilterKeys.some(k => filters[k]);

  // Everything EXCEPT the batch filter — the Export menu has its own batch
  // picker and needs the pool before batch narrowing.
  const filteredAnyBatch = students.filter(s => {
    const matchSearch  = matchesStudentSearch(s, search);
    const matchSex     = !filters.sex     || s.sex === filters.sex;
    const matchSection = !filters.section || sectionLetterOf(s) === filters.section;
    const matchCollege = !filters.college || s.college  === filters.college;
    const matchProgram = !filters.program || s.program  === filters.program;
    const matchSpec    = !filters.specialization || (s.specialization || s.major) === filters.specialization;

    // Status filter — non-exclusive, see matchesStatusFilter above.
    const matchStatus = matchesStatusFilter(applicationsByStudent[s.id], filters.status, companyStandings);

    return matchSearch && matchSex && matchSection && matchCollege && matchProgram && matchSpec && matchStatus;
  });
  const filtered = filters.batch ? filteredAnyBatch.filter(s => batchKeyOf(s) === filters.batch) : filteredAnyBatch;

  // Export pool: same search/filters as the screen, narrowed by the export's batch picker.
  const exportStudents = exportBatch ? filteredAnyBatch.filter(s => batchKeyOf(s) === exportBatch) : filteredAnyBatch;
  const exportBatchCounts = {};
  filteredAnyBatch.forEach(s => { const k = batchKeyOf(s); exportBatchCounts[k] = (exportBatchCounts[k] || 0) + 1; });
  const exportBatchKeys = sortBatchKeys(Object.keys(exportBatchCounts));

  // Sections that really have students, given the other panel filters (sex, batch,
  // program, major), with a head-count each. A selected section stays listed so it
  // can be switched off.
  const sectionCounts = new Map();
  students
    .filter(s =>
      (!filters.sex || s.sex === filters.sex) &&
      (!filters.batch || batchKeyOf(s) === filters.batch) &&
      (!filters.program || s.program === filters.program) &&
      (!filters.specialization || (s.specialization || s.major) === filters.specialization))
    .forEach(s => { const k = sectionLetterOf(s); if (k) sectionCounts.set(k, (sectionCounts.get(k) || 0) + 1); });
  if (filters.section && !sectionCounts.has(filters.section)) sectionCounts.set(filters.section, 0);
  const sectionOptions = [...sectionCounts.keys()].sort().map(k => ({ key: k, label: k, count: sectionCounts.get(k) }));

  // Batch options from the loaded students, newest academic year first.
  const batchOptions = sortBatchKeys([...new Set(students.map(batchKeyOf))]).filter(k => k !== BATCH_NONE);
  const hasUnbatched = students.some(s => batchKeyOf(s) === BATCH_NONE);

  // Group the visible rows by batch so this screen reads the same way as
  // Student Accounts.
  const groupedByBatch = (() => {
    const groups = {};
    filtered.forEach(s => { (groups[batchKeyOf(s)] ||= []).push(s); });
    return sortBatchKeys(Object.keys(groups)).map(key => ({ key, label: batchLabelOf(key), rows: groups[key] }));
  })();
  useEffect(() => { setCurPage(1); }, [search, filters]);

  // ── Pagination (10 students per page) ──
  // Pages run over the batch-ordered list; the rows of a page are grouped by batch again.
  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const safePage = Math.min(curPage, totalPages);
  const showingFrom = filtered.length ? (safePage - 1) * PAGE_SIZE + 1 : 0;
  const showingTo = Math.min(safePage * PAGE_SIZE, filtered.length);
  const pagedGroups = (() => {
    let skip = (safePage - 1) * PAGE_SIZE, left = PAGE_SIZE;
    const out = [];
    for (const g of groupedByBatch) {
      if (left <= 0) break;
      if (skip >= g.rows.length) { skip -= g.rows.length; continue; }
      const rows = g.rows.slice(skip, skip + left);
      skip = 0; left -= rows.length;
      out.push({ ...g, rows, all: g.rows, total: g.rows.length });
    }
    return out;
  })();

  const clearAllFilters = () => setFilters({ college: "", program: "", specialization: "", sex: "", section: "", status: "", batch: "" });

  // The export uses the search box + filter chips, then the batch picked in the
  // Export menu (it starts on the on-screen batch filter). "All batches" gives
  // one section per batch in the PDF.
  const exportDate = () => new Date().toISOString().slice(0, 10);

  const handleExportPdf = async (mode = "history") => {
    if (exportStudents.length === 0 || exportingPdf) return;
    setShowExport(false);
    setExportingPdf(true);
    try {
      const builder = mode === "final" ? buildFinalPlacementRows : mode === "status" ? buildPlacementStatusRows : buildExportRows;
      const batchKeys = exportBatch ? [exportBatch] : sortBatchKeys([...new Set(exportStudents.map(batchKeyOf))]);
      const groups = batchKeys.map(key => {
        const studs = exportStudents.filter(s => batchKeyOf(s) === key);
        return { label: batchLabelOf(key), studentCount: studs.length, rows: builder(studs, applicationsByStudent, companies, postsById) };
      }).filter(g => g.rows.length > 0);
      if (groups.length === 0) {
        setNotice(mode === "final"
          ? { title: "No accepted placements yet", message: "None of the students shown has an accepted placement yet." }
          : { title: "Nothing to export", message: "There's nothing to export for the students shown." });
        return;
      }
      let scope = describeExportScope({ ...filters, batch: exportBatch }, search);
      if (!exportBatch) scope = scope === "No filters applied" ? "All batches" : `${scope} · All batches`;
      const blob = await buildStudentPdf({ groups, mode, scope, total: exportStudents.length });
      const fileTag = mode === "final" ? "final-placements" : mode === "status" ? "placement-status" : "application-history";
      const batchTag = exportBatch ? (exportBatch === BATCH_NONE ? "no-batch" : exportBatch) : "all-batches";
      downloadBlob(`ojtern-${fileTag}-${batchTag}-${exportDate()}.pdf`, blob);
    } catch (err) {
      // The PDF libraries are fetched on click, so this also covers a failed
      // chunk load on a bad connection — silence here would look like a dead
      // button with no way to tell whether anything happened.
      console.error("PDF export failed:", err);
      setNotice({ title: "Couldn't generate the PDF", message: "Please check your connection and try again." });
    } finally {
      setExportingPdf(false);
    }
  };

  // ── One full-width row per student ────────────────────────────────────────
  const renderStudentRow = (student) => {
    const fullName = getFullName(student);
    // The row badge shows the single most-advanced application status,
    // so a coordinator can scan placement progress without opening anyone.
    const best = getBestApplication(applicationsByStudent[student.id]);
    const sc   = best ? (STATUS_COLORS[best.status] || { bg: color.wine400, color: ink }) : null;
    // Same single meta line as a Students Account row. College isn't in the
    // line (same as there) but stays in the row's tooltip.
    const metaText = [student.studentId, student.program, student.yearSection].filter(Boolean).join(" · ");

    return (
      <div key={student.id} className="sp-row" onClick={() => setViewingStudent(student)} title={student.college || undefined}>
        <StudentAvatar size={45} userIcon={themedUserIcon} />
        <div className="sp-row-main">
          <h3 style={{
            fontFamily: font.ui, fontSize: "1rem", fontWeight: 600,
            letterSpacing: "-0.01em", color: ink, lineHeight: 1.35,
            overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
          }}>
            {fullName}
          </h3>
          <p style={{
            fontFamily: font.ui, ...type.helper, color: inkMuted, marginTop: "2px",
            overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
          }}>
            {metaText || "—"}
          </p>
        </div>
        <div className="sp-row-actions">
        {needsFollowUp(applicationsByStudent[student.id], companyStandings) && (
          <span
            title={(applicationsByStudent[student.id] || []).map((a, _i, all) => followUpReason(a, all, companyStandings)).filter(Boolean).join("\n")}
            style={{ background: "#FFF3D6", color: "#8a5a00", border: "1px solid #F0D48A", borderRadius: radius.pill, padding: "3px 10px", fontFamily: font.ui, fontSize: "0.72rem", fontWeight: 600, flexShrink: 0, whiteSpace: "nowrap" }}
          >
            ⚑ Follow-up
          </span>
        )}
        {sc ? (
          <span style={{ background: sc.bg, color: sc.color, borderRadius: radius.pill, padding: "3px 11px", fontFamily: font.ui, fontSize: "0.75rem", fontWeight: 500, flexShrink: 0 }}>
            {best.status}
          </span>
        ) : (
          <span style={{ background: surface, border: `1px solid ${line}`, color: inkMuted, borderRadius: radius.pill, padding: "3px 11px", fontFamily: font.ui, fontSize: "0.75rem", flexShrink: 0 }}>
            Not applied
          </span>
        )}
        <span
          className="sp-row-action"
          onClick={(e) => { e.stopPropagation(); setViewingStudent(student); }}
          role="button"
          aria-label="View placement"
          style={{ display: "inline-flex", alignItems: "center", cursor: "pointer", flexShrink: 0 }}
        >
          <img src={themedViewIcon} alt="" style={{ width: "35px", height: "35px", objectFit: "contain" }} />
        </span>
        </div>
      </div>
    );
  };

  return (
    <>
      <ResponsiveStyles />
      <div className="sp-list-wrapper">
        <div style={fixedHead}>

        {/* Header bar — same floating dark panel as Find Company */}
        <div className="sp-search-bar">
          <div style={{ minWidth: 0, flex: "1 1 auto" }}>
            <span title="Students List" style={{ fontFamily: font.ui, fontSize: "clamp(1.1rem, 3.5vw, 1.375rem)", fontWeight: 600, letterSpacing: "-0.01em", color: onPanel, display: "block", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>Students List</span>
            {!loadingStudents && (
              <p title={`${filtered.length} of ${students.length} in your departments`} style={{ fontFamily: font.ui, ...type.helper, color: onPanelDim, marginTop: "2px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {filtered.length} of {students.length} in your departments
              </p>
            )}
          </div>

          <div style={{ position: "relative", display: "flex", alignItems: "center", flexShrink: 0 }}>
            <div id="sl-search-bar" style={{ display: "flex", alignItems: "center", gap: space.sm, background: color.white, borderRadius: radius.pill, padding: "9px 16px" }}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={inkMuted} strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
              </svg>
              <input
                value={search}
                onChange={e => setSearch(e.target.value)}
                placeholder="Search name or ID"
                className="sp-search-input"
                style={{ border: "none", background: "transparent", outline: "none", boxShadow: "none", WebkitAppearance: "none", appearance: "none", color: ink, fontFamily: font.ui, ...type.control }}
              />
              {search && (
                <button onClick={() => setSearch("")} aria-label="Clear search" style={{ background: "none", border: "none", color: inkMuted, cursor: "pointer", fontSize: "0.9rem", padding: 0, lineHeight: 1 }}>✕</button>
              )}
            </div>

            <div id="sl-export-btn" ref={exportRef} style={{ position: "relative", marginLeft: "10px" }}>
              <button
                onClick={() => { if (!showExport) setExportBatch(filters.batch || ""); setShowExport(v => !v); }}
                disabled={filteredAnyBatch.length === 0 || exportingPdf}
                title="Export students"
                aria-label="Export students"
                aria-expanded={showExport}
                style={{
                  width: "40px", height: "40px",
                  background: showExport ? color.goldTint : color.white,
                  border: showExport ? `1px solid ${color.onWineFaint}` : "none",
                  borderRadius: radius.pill,
                  display: "flex", alignItems: "center", justifyContent: "center",
                  cursor: filteredAnyBatch.length === 0 || exportingPdf ? "not-allowed" : "pointer",
                  opacity: filteredAnyBatch.length === 0 || exportingPdf ? 0.45 : 1,
                  flexShrink: 0, padding: 0,
                }}
              >
                <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke={showExport ? onPanel : inkMuted} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                  <polyline points="7 10 12 15 17 10"/>
                  <line x1="12" y1="15" x2="12" y2="3"/>
                </svg>
              </button>

              {showExport && (
                <div style={{
                  position: "absolute", top: "48px", right: 0, zIndex: 40,
                  background: color.white, border: `1px solid ${color.wine400}`,
                  borderRadius: radius.card, boxShadow: shadow.panel,
                  padding: "6px", minWidth: "270px",
                }}>
                  <div style={{ padding: "6px 10px 10px" }}>
                    <label htmlFor="sl-export-batch" style={{ display: "block", fontFamily: font.ui, ...type.helper, color: inkMuted, marginBottom: "4px" }}>
                      Batch to export
                    </label>
                    <select
                      id="sl-export-batch"
                      value={exportBatch}
                      onChange={e => setExportBatch(e.target.value)}
                      style={{ width: "100%", padding: "8px 10px", borderRadius: radius.pill, border: `1px solid ${lineStrong}`, background: color.white, color: ink, fontFamily: font.ui, ...type.control, cursor: "pointer" }}
                    >
                      <option value="">All batches (grouped by batch)</option>
                      {exportBatchKeys.map(k => (
                        <option key={k} value={k}>{batchLabelOf(k)} ({exportBatchCounts[k]})</option>
                      ))}
                    </select>
                    <p style={{ fontFamily: font.ui, ...type.helper, color: inkMuted, marginTop: "6px" }}>
                      {exportStudents.length} of {students.length} students
                    </p>
                  </div>
                  {[
                    { label: "Final placements (PDF)",      hint: "Accepted students and where they'll be deployed", onClick: () => handleExportPdf("final") },
                    { label: "Placement status (PDF)",      hint: "Every student — placed or not yet",               onClick: () => handleExportPdf("status") },
                    { label: "Full application history (PDF)", hint: "Every application of each student",           onClick: () => handleExportPdf("history") },
                  ].map(({ label, hint, onClick }) => (
                    <button
                      key={label}
                      onClick={onClick}
                      style={{
                        display: "block", width: "100%", textAlign: "left",
                        background: "none", border: "none", cursor: "pointer",
                        padding: "8px 10px", borderRadius: radius.pill,
                      }}
                    >
                      <span style={{ display: "block", fontFamily: font.ui, ...type.control, color: ink }}>{label}</span>
                      <span style={{ display: "block", fontFamily: font.ui, ...type.helper, color: inkMuted }}>{hint}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>

            <div id="sl-filter-btn" style={{ position: "relative", marginLeft: "10px" }}>
              <div
                onClick={() => setShowFilter(v => !v)}
                title="Filters"
                style={{ width: "40px", height: "40px", background: hasFilter ? color.goldTint : color.white, borderRadius: radius.pill, display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", border: hasFilter ? `1px solid ${color.onWineFaint}` : "none" }}
              >
                <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke={hasFilter ? onPanel : inkMuted} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3"/>
                </svg>
              </div>
              {showFilter && <FilterPanel filters={filters} setFilters={setFilters} filterRef={filterRef} coordinatorColleges={coordinatorColleges} batchOptions={batchOptions} hasUnbatched={hasUnbatched} sectionOptions={sectionOptions} />}
            </div>
          </div>
        </div>

        {/* Status chips */}
        <div id="sl-status-chips" style={{ display: "flex", gap: space.sm, alignItems: "center", flexWrap: "wrap", marginBottom: space.md, width: "fit-content", maxWidth: "100%" }}>
          {["All", "Accepted", "In Progress", "All Declined", "Needs Follow-up", "No Applications yet"].map((statusOption) => {
            const isActive = statusOption === "All" ? filters.status === "" : filters.status === statusOption;
            // Each option keeps the colour of the status it represents, so the
            // chips and the row badges read as the same language.
            const statusColor =
              statusOption === "Accepted"            ? "#358D5E" :
              statusOption === "In Progress"         ? "#CCC929" :
              statusOption === "All Declined"        ? "#FF0000" :
              statusOption === "Needs Follow-up"     ? "#B7791F" :
              statusOption === "No Applications yet" ? "#A9A9A9" : panel;
            const activeText = statusOption === "All" ? onPanel : color.white;

            return (
              <button
                key={statusOption}
                onClick={() => setFilters(p => ({ ...p, status: statusOption === "All" ? "" : statusOption }))}
                style={{
                  background: isActive ? statusColor : surface,
                  color: isActive ? activeText : inkBody,
                  border: isActive ? "none" : `1.5px solid ${lineStrong}`,
                  borderRadius: radius.pill,
                  padding: "7px 16px",
                  fontFamily: font.ui,
                  ...type.helper,
                  fontWeight: isActive ? 500 : 400,
                  cursor: "pointer",
                  transition: `all 180ms ${ease}`,
                  whiteSpace: "nowrap",
                  flexShrink: 0,
                }}
                onMouseEnter={e => { if (!isActive) e.currentTarget.style.borderColor = color.hoverBorder; }}
                onMouseLeave={e => { if (!isActive) e.currentTarget.style.borderColor = lineStrong; }}
              >
                {statusOption}
              </button>
            );
          })}
        </div>

        </div>

        <div ref={scrollRef} style={listScroll}>
        {/* Student list */}
        {loadingStudents ? (
          <div style={{ display: "flex", alignItems: "center", justifyContent: "center", padding: "72px 0" }}>
            <p style={{ fontFamily: font.ui, ...type.body, color: inkFaint }}>Loading students…</p>
          </div>
        ) : filtered.length > 0 ? (
          <>
          <div id="sl-student-list" className="sp-rows">
            {pagedGroups.map(group => (
              <section key={group.key} style={{ marginBottom: space.lg }}>
                <div style={{
                  display: "flex", alignItems: "center", gap: space.sm, flexWrap: "wrap",
                  padding: "0 2px 8px", borderBottom: `1px solid ${line}`, marginBottom: space.sm,
                }}>
                  <h3 style={{ fontFamily: font.ui, ...type.label, color: ink, fontWeight: 650, margin: 0, minWidth: 0, overflowWrap: "anywhere" }}>{group.label}</h3>
                  <span style={{ fontFamily: font.ui, ...type.helper, color: inkMuted, whiteSpace: "nowrap" }}>
                    {group.total} student{group.total !== 1 ? "s" : ""}
                  </span>
                </div>
                <div className="sp-rows">
                  {group.rows.map(renderStudentRow)}
                </div>
              </section>
            ))}
          </div>
          <PaginationBar page={safePage} totalPages={totalPages} onPage={goToPage} from={showingFrom} to={showingTo} total={filtered.length} />
          </>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", textAlign: "center", padding: "72px 24px", gap: space.xs, background: surface, border: `1px dashed ${color.wine400}`, borderRadius: radius.panel }}>
            {students.length === 0 ? (
              <>
                <p style={{ fontFamily: font.ui, fontSize: "1.0625rem", fontWeight: 600, color: ink }}>No students in your departments yet</p>
                <p style={{ fontFamily: font.ui, ...type.helper, color: inkMuted, maxWidth: "44ch" }}>Students appear here once their accounts are created under the departments assigned to you.</p>
              </>
            ) : (
              <>
                <p style={{ fontFamily: font.ui, fontSize: "1.0625rem", fontWeight: 600, color: ink }}>No students match this search</p>
                <p style={{ fontFamily: font.ui, ...type.helper, color: inkMuted, maxWidth: "44ch" }}>Try a different name or ID, or clear a filter to widen the results.</p>
                <button onClick={clearAllFilters} style={{ marginTop: space.sm, background: panel, color: onPanel, border: "none", borderRadius: radius.pill, padding: "9px 20px", fontFamily: font.ui, ...type.control, cursor: "pointer" }}>
                  Clear filters
                </button>
              </>
            )}
          </div>
        )}
        </div>
      </div>

      {notice && <NoticeModal title={notice.title} message={notice.message} onClose={() => setNotice(null)} />}

      {viewingStudent && (
        <PlacementModal
          student={viewingStudent}
          userIcon={themedUserIcon}
          companies={companies}
          postsById={postsById}
          onClose={() => setViewingStudent(null)}
          onNavigateToCompany={(companyId, studentId) => {
            setViewingStudent(null);
            onNavigateToCompany && onNavigateToCompany(companyId, studentId);
          }}
          onMessageStudent={onMessageStudent}
        />
      )}
    </>
  );
};

export default CoordinatorStudentListScreen;